// catalogRuleRank.js — free rule layer for catalog identification search.
//
// Ported from the offline benchmark FOS-IDENT-HYBRID-001
// (branch eval/fos-jev-eval-001, ops/evals/fos-ident-hybrid-001/src/rules.mjs).
// On a fresh synthetic set of 792 owned bottles it raised right-bottle-first
// from 77.5% to 84.3% and first-screen from 93.3% to 97.7%, and cut false
// "not in catalog" on real bottles from 10.2% to 1.0%, with no model call.
//
// Three rules, applied on top of the existing search output:
//  (a) concentration wording is canonicalised on BOTH sides (query and catalog
//      name) and used as a preference, never as a hard filter;
//  (b) line intent: a query that equals a line groups that line's rows first,
//      base row first;
//  (c) collector shorthand expands to house/product names; the expanded query
//      is searched in addition to the typed one (union of both result sets).
//
// Pure functions, no I/O.
import { normalizeCatalogSearchText } from './catalogSearchConfidence.js'
import { deriveLineName } from './houseHierarchy.js'
import { resolvedConcentrationDisplay } from './concentrationResolver.js'
import { expandSearchQuery } from './searchQueryAliases.js'
import { HOUSE_LABEL_ALIASES, HOUSE_SHORTHAND, PRODUCT_SHORTHAND } from './catalogAliases.js'

export const RULES_VERSION = 'fos-search-rule-layer-001/rules-v1'

// Re-export for backward compatibility
export { HOUSE_LABEL_ALIASES, HOUSE_SHORTHAND, PRODUCT_SHORTHAND }

// ---- (c) shorthand ----------------------------------------------------------
const SYMBOL_FORMS = [
  [/\bd\s*&\s*g\b/gi, 'dg'], [/\bv\s*&\s*r\b/gi, 'vr'], [/\ba\s*&\s*f\b/gi, 'af'], [/\bvc\s*&\s*a\b/gi, 'vca'],
]
const SPACED_FORMS = [[/\bbr 540\b/g, 'br540'], [/\bnine pm\b/g, 'ninepm'], [/\b9 pm\b/g, '9pm'], [/\bcdn im\b/g, 'cdnim']]

// ---- (a) concentration vocabulary --------------------------------------------
const CONC_PHRASES = [
  ['extrait de parfum', 'extrait'], ['eau de parfum', 'edp'], ['eau de toilette', 'edt'], ['eau de cologne', 'edc'],
  ['eau de perfume', 'edp'], ['perfume oil', 'oil'], ['fragrance oil', 'oil'], ['le parfum', 'parfum'],
  ['pure parfum', 'parfum'], ['e d p', 'edp'], ['e d t', 'edt'],
]
const CONC_TOKEN = Object.freeze({ edp: 'edp', edt: 'edt', edc: 'edc', cologne: 'edc', extrait: 'extrait', parfum: 'parfum', oil: 'oil', attar: 'oil' })

// "the" is deliberately NOT a stopword: it is part of names such as
// "The Most Wanted" and "Thé Noir" (defect found after the offline run).
const STOP = new Set(['de', 'di', 'du', 'des', 'la', 'le', 'les', 'l', 'pour', 'for', 'by', 'of', 'and', 'a', 'al', 'el', 'd'])

const ELISION = /\b([ldjnmst]|qu)['’`]/gi

export function effectiveConcentration(row) {
  const shown = resolvedConcentrationDisplay(row || {})
  if (shown && !['unknown', 'ambiguous'].includes(String(shown).toLowerCase())) return shown
  return String(row?.concentration || '').trim()
}

export function canonicalTokens(text, { elision = true } = {}) {
  const raw = String(text || '')
  let s = normalizeCatalogSearchText(elision ? raw.replace(ELISION, '$1 ') : raw)
  for (const [phrase, tok] of CONC_PHRASES) s = s.replace(new RegExp(`\\b${phrase}\\b`, 'g'), tok)
  return s.split(' ').filter(Boolean)
}

export const concCodeOfText = text => {
  const codes = canonicalTokens(text).filter(x => CONC_TOKEN[x]).map(x => CONC_TOKEN[x])
  return codes.length ? codes[codes.length - 1] : ''
}

function foldSymbols(raw) {
  let s = String(raw || '')
  for (const [re, tok] of SYMBOL_FORMS) s = s.replace(re, ` ${tok} `)
  return s
}

// Multi-word house names ("christian dior" → "dior") are replaced as whole
// phrases before the token-by-token shorthand lookup, which cannot see them.
const PHRASE_ALIASES = Object.entries(HOUSE_LABEL_ALIASES).map(([phrase, target]) => [
  new RegExp(`(^| )${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?= |$)`, 'g'), target,
])
function expandPhraseAliases(base) {
  let out = base
  for (const [re, target] of PHRASE_ALIASES) out = out.replace(re, `$1${target}`)
  return out
}

export function parseQuery(raw) {
  let base = normalizeCatalogSearchText(foldSymbols(raw))
  for (const [re, tok] of SPACED_FORMS) base = base.replace(re, tok)
  const typed = canonicalTokens(base)
  const expandedWords = expandPhraseAliases(base).split(' ').filter(Boolean).flatMap(t => (PRODUCT_SHORTHAND[t] || HOUSE_SHORTHAND[t] || t).split(' '))
  const expanded = canonicalTokens(expandedWords.join(' '))
  const concCodes = [...new Set(expanded.filter(t => CONC_TOKEN[t]).map(t => CONC_TOKEN[t]))]
  const strip = toks => {
    const kept = toks.filter(t => !CONC_TOKEN[t])
    return kept.length ? kept : toks
  }
  const onlyConc = expanded.every(t => CONC_TOKEN[t])
  const expandedForSearch = canonicalTokens(expandedWords.join(' '), { elision: false })
  const rewritten = onlyConc ? base : strip(expandedForSearch).join(' ')
  return {
    typed: strip(typed),
    expanded: strip(expanded),
    concCodes: onlyConc ? [] : concCodes,
    usedShorthand: expanded.join(' ') !== typed.join(' '),
    rewritten,
    rewrittenDiffers: rewritten !== base && rewritten.length >= 3,
  }
}

// True when some row already contains every content token. A one-token
// query is covered by any hit of that token, so it does not need a wider page.
export function queryCoveredByRows(rawQuery, rows = []) {
  const tokens = parseQuery(rawQuery).typed.filter(token => !STOP.has(token))
  if (tokens.length < 2) return true
  return (rows || []).some(row => {
    const words = new Set(canonicalTokens(`${row?.house || ''} ${row?.name || ''}`))
    return tokens.every(token => words.has(token))
  })
}

// The second search the rule layer issues, or null when it would repeat the first.
export function secondSearchQuery(rawQuery) {
  const pq = parseQuery(rawQuery)
  if (!pq.rewrittenDiffers) return null
  const a = normalizeCatalogSearchText(expandSearchQuery(String(rawQuery).trim()))
  const b = normalizeCatalogSearchText(expandSearchQuery(pq.rewritten))
  return a === b ? null : pq.rewritten
}

export function rowFeatures(row) {
  const houseT = canonicalTokens(row.house)
  const nameAll = canonicalTokens(row.name)
  const nameConc = nameAll.filter(t => CONC_TOKEN[t]).map(t => CONC_TOKEN[t])
  const nameIdent = nameAll.filter(t => !CONC_TOKEN[t])
  const effCode = concCodeOfText(effectiveConcentration(row))
  const line = canonicalTokens(deriveLineName(row)).filter(t => !CONC_TOKEN[t]).join(' ')
  const ident = nameIdent.length ? nameIdent : nameAll
  const alts = []
  for (const toks of [houseT, ident]) {
    for (let i = 0; i + 1 < toks.length; i++) if (/^[ld]$/.test(toks[i])) alts.push({ joined: toks[i] + toks[i + 1], parts: [toks[i], toks[i + 1]] })
  }
  return { houseT, nameIdent: ident, alts, concCodes: new Set([effCode, ...nameConc].filter(Boolean)), line, houseKey: houseT.join(' ') }
}

function osa(a, b) {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)])
  for (let j = 1; j <= b.length; j++) d[0][j] = j
  for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++) {
    d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
    if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1)
  }
  return d[a.length][b.length]
}

export function tokenMatch(q, r) {
  if (q === r) return 1
  if (q.length >= 3 && r.startsWith(q)) return 0.9
  if (q.length >= 4 && r.length >= 4 && Math.abs(q.length - r.length) <= 1 && osa(q, r) <= 1) return 0.8
  return 0
}

function coverage(qTokens, f) {
  const rowT = [...f.houseT, ...f.nameIdent, ...f.alts.map(a => a.joined)]
  const content = qTokens.filter(t => !STOP.has(t))
  const qs = content.length ? content : qTokens
  let qsum = 0
  for (const t of qs) qsum += Math.max(0, ...rowT.map(r => tokenMatch(t, r)))
  const qcov = qs.length ? qsum / qs.length : 0
  const nameContent = f.nameIdent.filter(t => !STOP.has(t) && !f.houseT.includes(t))
  const ns = nameContent.length ? nameContent : f.nameIdent
  const covered = r => qTokens.some(t => tokenMatch(t, r) > 0)
    || f.alts.some(a => a.parts.includes(r) && qTokens.some(t => tokenMatch(t, a.joined) > 0))
  const ncov = ns.length ? ns.filter(covered).length / ns.length : 0
  return { qcov, ncov }
}

export const WEIGHTS = Object.freeze({ qcov: 2, ncov: 1, concMatch: 1, concMismatch: -0.5, prior: 0.3 })

export function scoreRow(pq, row, prior = 0, f = rowFeatures(row)) {
  const a = coverage(pq.typed, f)
  const b = coverage(pq.expanded, f)
  const best = (2 * b.qcov + b.ncov) > (2 * a.qcov + a.ncov) ? b : a
  let conc = 0
  if (pq.concCodes.length && f.concCodes.size) conc = pq.concCodes.some(c => f.concCodes.has(c)) ? WEIGHTS.concMatch : WEIGHTS.concMismatch
  const score = WEIGHTS.qcov * best.qcov + WEIGHTS.ncov * best.ncov + conc + WEIGHTS.prior * prior
  return { score, qcov: best.qcov, ncov: best.ncov, conc }
}

function queryLineFor(pq, f) {
  for (const toks of [pq.expanded, pq.typed]) {
    const withoutHouse = toks.filter(t => !f.houseT.includes(t))
    if (f.line && withoutHouse.join('') === f.line.replace(/ /g, '')) return true
  }
  return false
}

// rowsA: rows for the typed query (already in app order); rowsR: rows for the
// rewritten query (app order) or []. Rows must carry name/house/concentration
// fields (the search RPC rows do). Returns the merged, re-ranked row objects.
export function ruleRank(rawQuery, rowsA = [], rowsR = []) {
  const pq = parseQuery(rawQuery)
  const pool = new Map()
  const addList = list => (list || []).forEach((r, i) => {
    if (!r?.id) return
    const prior = list.length > 1 ? 1 - i / list.length : 1
    const cur = pool.get(r.id)
    if (!cur) pool.set(r.id, { row: r, prior })
    else cur.prior = Math.max(cur.prior, prior)
  })
  addList(rowsA)
  addList(rowsR)
  const scored = []
  for (const p of pool.values()) {
    const f = rowFeatures(p.row)
    scored.push({ ...p, ...scoreRow(pq, p.row, p.prior, f), line: `${f.houseKey}|${f.line}`, lineHit: queryLineFor(pq, f) })
  }
  scored.sort((x, y) => y.score - x.score || y.prior - x.prior || String(x.row.id).localeCompare(String(y.row.id)))
  const lineRows = scored.filter(s => s.lineHit)
  if (lineRows.length < 2) return scored.map(s => s.row)
  const groups = [...new Set(lineRows.map(s => s.line))].map(k => lineRows.filter(s => s.line === k))
  groups.sort((g1, g2) => Math.max(...g2.map(s => s.score)) - Math.max(...g1.map(s => s.score)))
  const inLine = groups.flatMap(g => [...g].sort((x, y) => (y.ncov === 1) - (x.ncov === 1) || y.score - x.score || y.prior - x.prior))
  const ids = new Set(inLine.map(s => s.row.id))
  return [...inLine, ...scored.filter(s => !ids.has(s.row.id))].map(s => s.row)
}
